
import { Order, Driver, SystemConfig, DeliveryProvider, OperatingContext, SmartSplitSuggestion, SuggestedRoute, DecisionTraceEntry, DecisionScoringWeights } from '../../../types';
import { formClusters } from '../math/clustering';
import { buildCanonicalCostScoringInput, calculateInternalCostV2, findBestProviderQuoteAsync } from '../costs/calculator';
import { getFleetAvailability } from '../fleet/availability';
import { getTravelMinutes } from '../core/routing';
import { calculateSystemPulse } from '../fleet/metrics';
import {
    analyzeSafeDispatch,
    buildCanonicalSlaSlackScoringInput,
    calculateSafeDispatchSequence,
    calculateSafeReturnWindow,
    isRouteRiskyForPeak,
    getMinutesUntilClose
} from '../core/timing';
import { DistanceService } from '../../logistics/core/distanceService';
import {
    createBoomerangDecision,
    createDispatchHoldDecision,
    evaluateProductionRetention,
    getClusterReadiness,
    getDivergentThresholds,
    getOldestElapsedMinutes,
    isHoldAllowedByGuardrail,
    isStrategyIgnored
} from './divergent';

/**
 * v46.0: SmartSplit - Full Strategic Compliance
 * - Implementa Retenção de Produção, Despacho Retido e Bumerangue.
 * - Respeita Limite de Raio por Motoboy (maxRangeKm).
 * - Garante independência de estratégias através de ignoredStrategies.
 */

const groupManualOrdersByRoute = (orders: Order[], mode: 'own' | 'third') => {
    const grouped = new Map<string, Order[]>();
    for (const order of orders) {
        const key = order.manualRouteGroup || `${mode}_route_1`;
        const current = grouped.get(key) || [];
        grouped.set(key, [...current, order]);
    }

    const extractOrder = (group: string) => {
        const match = group.match(/route_(\d+)/);
        if (!match) return 1;
        const parsed = Number(match[1]);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
    };

    return [...grouped.entries()]
        .sort((a, b) => extractOrder(a[0]) - extractOrder(b[0]))
        .map(([groupId, routeOrders]) => ({ groupId, routeOrders }));
};

type ArbitrationSignals = {
    sla: number;
    margin: number;
    capacity: number;
};

type ArbitrationScore = {
    total: number;
    weighted: ArbitrationSignals;
    signals: ArbitrationSignals;
    weights: DecisionScoringWeights;
};

type ArbitrationCandidate<TId extends string> = {
    id: TId;
    score: ArbitrationScore;
    tieBreaker?: number;
};

const DEFAULT_DECISION_SCORING_WEIGHTS: DecisionScoringWeights = {
    slaPriority: 0.5,
    marginPriority: 0.3,
    capacityPriority: 0.2
};

const clamp01 = (value: number): number => Math.max(0, Math.min(1, value));

const normalizeDecisionScoringWeights = (weights?: DecisionScoringWeights): DecisionScoringWeights => {
    const raw = weights ?? DEFAULT_DECISION_SCORING_WEIGHTS;
    const sanitized: DecisionScoringWeights = {
        slaPriority: Number.isFinite(raw.slaPriority) && raw.slaPriority >= 0 ? raw.slaPriority : DEFAULT_DECISION_SCORING_WEIGHTS.slaPriority,
        marginPriority: Number.isFinite(raw.marginPriority) && raw.marginPriority >= 0 ? raw.marginPriority : DEFAULT_DECISION_SCORING_WEIGHTS.marginPriority,
        capacityPriority: Number.isFinite(raw.capacityPriority) && raw.capacityPriority >= 0 ? raw.capacityPriority : DEFAULT_DECISION_SCORING_WEIGHTS.capacityPriority
    };

    const totalWeight = sanitized.slaPriority + sanitized.marginPriority + sanitized.capacityPriority;
    if (totalWeight <= 0) {
        return DEFAULT_DECISION_SCORING_WEIGHTS;
    }

    return {
        slaPriority: sanitized.slaPriority / totalWeight,
        marginPriority: sanitized.marginPriority / totalWeight,
        capacityPriority: sanitized.capacityPriority / totalWeight
    };
};

const buildArbitrationScore = (signals: ArbitrationSignals, weights: DecisionScoringWeights): ArbitrationScore => {
    const normalizedSignals: ArbitrationSignals = {
        sla: clamp01(signals.sla),
        margin: clamp01(signals.margin),
        capacity: clamp01(signals.capacity)
    };

    const weighted: ArbitrationSignals = {
        sla: normalizedSignals.sla * weights.slaPriority,
        margin: normalizedSignals.margin * weights.marginPriority,
        capacity: normalizedSignals.capacity * weights.capacityPriority
    };

    return {
        total: weighted.sla + weighted.margin + weighted.capacity,
        weighted,
        signals: normalizedSignals,
        weights
    };
};

const pickArbitrationCandidate = <TId extends string>(candidates: ArbitrationCandidate<TId>[]): ArbitrationCandidate<TId> | null => {
    if (candidates.length === 0) return null;

    return [...candidates].sort((a, b) => {
        if (b.score.total !== a.score.total) return b.score.total - a.score.total;
        return (b.tieBreaker ?? 0) - (a.tieBreaker ?? 0);
    })[0] ?? null;
};

const formatArbitrationSummary = (
    selectedLabel: string,
    score: ArbitrationScore,
    profileId: string
): string => {
    return `Arbitro(${profileId}): ${selectedLabel} score=${score.total.toFixed(3)} [sla=${score.signals.sla.toFixed(2)}*${score.weights.slaPriority.toFixed(2)}, margem=${score.signals.margin.toFixed(2)}*${score.weights.marginPriority.toFixed(2)}, capacidade=${score.signals.capacity.toFixed(2)}*${score.weights.capacityPriority.toFixed(2)}].`;
};

export const calculateSmartSplitAsync = async (
    orders: Order[],
    config: SystemConfig,
    drivers: Driver[],
    providers: DeliveryProvider[],
    context: OperatingContext,
    storeId?: string,
    oracle?: import('../../../types').OracleState,
    kitchen?: import('../../../types').KitchenMetrics
): Promise<SmartSplitSuggestion> => {

    const allPending = orders.filter(o => {
        if (o.type !== 'DELIVERY') return false;
        if (['confirmed', 'scheduled_confirmed', 'ready'].includes(o.status)) return true;
        if (o.status === 'assigned' && !o.pickupConfirmed) return true;
        if (o.status === 'dispatched' && !o.pickupConfirmed) return true;
        return false;
    });
    if (allPending.length === 0) return createEmptySuggestion();

    const manualOrders = allPending.filter(o => !!o.manualAssignment);
    const autoPending = allPending.filter(o => !o.manualAssignment);

    const routes: SuggestedRoute[] = [];
    const ownFleetOrders: Order[] = [];
    const thirdPartyOrders: Order[] = [];
    const heldOrders: Order[] = [];
    const processedOrderIds = new Set<string>();
    const decisionTrace: DecisionTraceEntry[] = [];
    const activeProviders = providers.filter(p => p.active);

    const fleetSlots = getFleetAvailability(drivers, orders, config.businessLat, config.businessLng, config);
    const pulse = calculateSystemPulse(orders, drivers, config);
    const minsToClose = getMinutesUntilClose(config);
    const isDrainageActive = minsToClose < (config.drainageThresholdMinutes || 25);
    const minSavings = config.enableThirdPartyMinSavings === false ? 0 : (config.thirdPartyMinSavings ?? 0);
    const deviationThreshold = config.enableThirdPartyDeviation === false
        ? Number.POSITIVE_INFINITY
        : (config.thirdPartyDeviationThreshold ?? 1.4);
    const maxHoldMinutes = Math.max(6, Math.floor((config.averageKitchenTime || 10) * 1.5));
    const maxHoldOrders = Math.max(2, config.fleetConfig.maxOrdersPerRoute || 4);
    const decisionPolicyProfile = config.oracle?.decisionPolicyProfile ?? 'balanced';
    const decisionScoringWeights = normalizeDecisionScoringWeights(config.oracle?.decisionScoringWeights);

    let totalOwnCash = 0, totalOwnDecision = 0, totalThirdPartyCash = 0, totalThirdPartyDecision = 0;
    let primaryDecisionReason: import('../../../types').DecisionReason = 'CHEAPER';

    // v59.0: ESTRATÉGIAS SUGERIDAS (Aguardando confirmação do operador)
    // Diferente de heldOrders, estas estratégias são sugeridas mas os pedidos
    // permanecem visíveis nas colunas até confirmação via modal Sofia
    const suggestedStrategies: import('../../../types').SmartSplitSuggestion['suggestedStrategies'] = [];

    // 0. SOBERANIA DO OPERADOR (Rotas Fixadas Manualmente)
    const manualOwn = manualOrders.filter(o => o.manualAssignment === 'own');
    const manualThird = manualOrders.filter(o => o.manualAssignment === 'third');

    if (manualOwn.length > 0) {
        const manualOwnGroups = groupManualOrdersByRoute(manualOwn, 'own');

        for (const { routeOrders, groupId } of manualOwnGroups) {
            let lastLat = config.businessLat, lastLng = config.businessLng;
            let outboundMins = 0, interMins = 0;
            let manualOwnThirdCost = 0;

            // PARALLEL: Pre-calculate all route legs to minimize API latency
            const legPromises = routeOrders.map((o, i) => {
                const originLat = i === 0 ? config.businessLat : routeOrders[i - 1].lat!;
                const originLng = i === 0 ? config.businessLng : routeOrders[i - 1].lng!;
                return getTravelMinutes({
                    originLat, originLng, destLat: o.lat!, destLng: o.lng!,
                    googleMapsApiKey: config.googleMapsApiKey,
                    hereApiKey: config.hereMapsApiKey,
                    storeId,
                    orderCreatedAt: o.createdAt,
                    routingApiProvider: config.routingApiProvider,
                    routingApiFallbackEnabled: config.routingApiFallbackEnabled,
                    routingOsrmCalibration: config.routingOsrmCalibration
                }).catch(error => {
                    console.error(`[SMARTSPLIT] Route leg calculation failed for order ${o.id}:`, error);
                    return { minutes: 0, distanceKm: 0 };
                });
            });

            const legs = await Promise.all(legPromises);

            // SEQUENTIAL: Process legs with stateful updates (lastLat/lastLng)
            const quotePromises: Promise<{ price: number; distForQuote: number }>[] = [];
            for (let i = 0; i < routeOrders.length; i++) {
                const o = routeOrders[i];
                const leg = legs[i];

                if (i === 0) outboundMins = leg.minutes; else interMins += leg.minutes;
                o.estimatedDuration = leg.minutes;

                // PARALLEL: Queue quote fetching (independent per order)
                const distForQuote = leg.distanceKm > 0
                    ? Promise.resolve(leg.distanceKm)
                    : DistanceService.getDistance(lastLat, lastLng, o.lat!, o.lng!, 'precise').catch(() => 0);

                quotePromises.push(
                    distForQuote.then(async (dist) => {
                        try {
                            const quote = await findBestProviderQuoteAsync(dist, o, providers, context, config, { lat: lastLat, lng: lastLng });
                            return { price: quote.price, distForQuote: dist };
                        } catch (error) {
                            console.error(`[SMARTSPLIT] Quote fetch failed for order ${o.id}:`, error);
                            return { price: 0, distForQuote: dist };
                        }
                    })
                );

                lastLat = o.lat!; lastLng = o.lng!;
            }

            // PARALLEL: Await all quote results
            const quotes = await Promise.all(quotePromises);
            manualOwnThirdCost = quotes.reduce((sum, q) => sum + q.price, 0);

            const manualBatchDiscount = config.thirdPartyConfig?.batchDiscountPercentage ?? config.thirdPartySmartRouteDiscount ?? 0;
            if (routeOrders.length > 1) manualOwnThirdCost *= (1 - (manualBatchDiscount / 100));

            const returnLeg = await getTravelMinutes({
                originLat: lastLat,
                originLng: lastLng,
                destLat: config.businessLat,
                destLng: config.businessLng,
                googleMapsApiKey: config.googleMapsApiKey,
                hereApiKey: config.hereMapsApiKey,
                storeId,
                orderCreatedAt: Date.now(),
                routingApiProvider: config.routingApiProvider,
                routingApiFallbackEnabled: config.routingApiFallbackEnabled,
                routingOsrmCalibration: config.routingOsrmCalibration
            });

            const internal = calculateInternalCostV2(
                { outbound: outboundMins, inter: interMins, return: returnLeg.minutes },
                pulse.isBusy, routeOrders.length, pulse.loadFactor, config, pulse.oracleMultiplier
            );

            const slotIdx = fleetSlots.findIndex(s => s.isAvailable);
            const slot = slotIdx !== -1 ? fleetSlots[slotIdx] : null;
            const routeNumber = groupId.replace(/^own_route_/, '');

            routes.push({
                type: 'own',
                assigneeId: slot?.driverId,
                assigneeName: slot ? slot.driver.name : 'Soberania: Frota Própria (Aguardando)',
                orders: routeOrders,
                cost: internal.cashCost,
                strategyLabel: `MANUAL: FROTA PROPRIA - ROTA ${routeNumber}`,
                decisionSummary: 'Soberania do operador: rota fixada manualmente para frota propria.',
                estimatedReturnMinutes: returnLeg.minutes,
                requiresOvertimeApproval: slot?.requiresOvertimeApproval
            });

            decisionTrace.push({
                kind: 'route',
                mode: 'own',
                decisionReason: 'UNKNOWN',
                orderIds: routeOrders.map(o => o.id),
                chosenLabel: `MANUAL_OWN_${routeNumber}`,
                rationale: 'Decisao manual mantida pelo despachante (soberania do operador).',
                ownCost: internal.cashCost,
                thirdCost: manualOwnThirdCost,
                constraints: ['manual_assignment', 'manual_route_group']
            });

            routeOrders.forEach(o => {
                o.optimizationOutcome = 'own';
                ownFleetOrders.push(o);
                processedOrderIds.add(o.id);
            });

            if (slotIdx !== -1) fleetSlots.splice(slotIdx, 1);

            totalOwnCash += internal.cashCost;
            totalOwnDecision += internal.totalDecisionCost;
        }
    }

    if (manualThird.length > 0) {
        const manualThirdGroups = groupManualOrdersByRoute(manualThird, 'third');

        for (const { routeOrders, groupId } of manualThirdGroups) {
            let lastLat = config.businessLat, lastLng = config.businessLng;
            let outboundMins = 0, interMins = 0;
            let clusterPrice = 0;

            // PARALLEL: Pre-calculate all route legs to minimize API latency
            const legPromises = routeOrders.map((o, i) => {
                const originLat = i === 0 ? config.businessLat : (routeOrders[i - 1].lat || config.businessLat);
                const originLng = i === 0 ? config.businessLng : (routeOrders[i - 1].lng || config.businessLng);
                return getTravelMinutes({
                    originLat,
                    originLng,
                    destLat: o.lat || config.businessLat,
                    destLng: o.lng || config.businessLng,
                    googleMapsApiKey: config.googleMapsApiKey,
                    hereApiKey: config.hereMapsApiKey,
                    storeId,
                    orderCreatedAt: o.createdAt,
                    routingApiProvider: config.routingApiProvider,
                    routingApiFallbackEnabled: config.routingApiFallbackEnabled,
                    routingOsrmCalibration: config.routingOsrmCalibration
                }).catch(error => {
                    console.error(`[SMARTSPLIT] Route leg calculation failed for order ${o.id}:`, error);
                    return { minutes: 0, distanceKm: 0 };
                });
            });

            const legs = await Promise.all(legPromises);

            // SEQUENTIAL: Process legs with stateful updates, queue quotes in parallel
            const quotePromises: Promise<{ price: number }>[] = [];
            for (let i = 0; i < routeOrders.length; i++) {
                const o = routeOrders[i];
                const leg = legs[i];

                if (i === 0) outboundMins = leg.minutes; else interMins += leg.minutes;
                o.estimatedDuration = leg.minutes;

                // PARALLEL: Queue quote fetching
                const distPromise = leg.distanceKm > 0
                    ? Promise.resolve(leg.distanceKm)
                    : DistanceService.getDistance(lastLat, lastLng, o.lat || config.businessLat, o.lng || config.businessLng, 'precise').catch(() => 0);

                quotePromises.push(
                    distPromise.then(async (dist) => {
                        try {
                            const q = await findBestProviderQuoteAsync(dist, o, providers, context, config, { lat: lastLat, lng: lastLng });
                            return { price: q.price };
                        } catch (error) {
                            console.error(`[SMARTSPLIT] Quote fetch failed for order ${o.id}:`, error);
                            return { price: 0 };
                        }
                    })
                );

                lastLat = o.lat || config.businessLat;
                lastLng = o.lng || config.businessLng;
            }

            // PARALLEL: Await all quote results
            const quotes = await Promise.all(quotePromises);
            clusterPrice = quotes.reduce((sum, q) => sum + q.price, 0);

            const returnLeg = await getTravelMinutes({
                originLat: lastLat,
                originLng: lastLng,
                destLat: config.businessLat,
                destLng: config.businessLng,
                googleMapsApiKey: config.googleMapsApiKey,
                hereApiKey: config.hereMapsApiKey,
                storeId,
                orderCreatedAt: Date.now(),
                routingApiProvider: config.routingApiProvider,
                routingApiFallbackEnabled: config.routingApiFallbackEnabled,
                routingOsrmCalibration: config.routingOsrmCalibration
            });

            const internalAlternative = calculateInternalCostV2(
                { outbound: outboundMins, inter: interMins, return: returnLeg.minutes },
                pulse.isBusy,
                routeOrders.length,
                pulse.loadFactor,
                config,
                pulse.oracleMultiplier
            );

            const batchDiscount = config.thirdPartyConfig?.batchDiscountPercentage ?? config.thirdPartySmartRouteDiscount ?? 0;
            if (routeOrders.length > 1) clusterPrice *= (1 - (batchDiscount / 100));

            const routeNumber = groupId.replace(/^third_route_/, '');

            routes.push({
                type: 'third',
                assigneeName: `Soberania: Envio Externo (Rota ${routeNumber})`,
                orders: routeOrders,
                cost: clusterPrice,
                strategyLabel: `MANUAL: ENVIO EXTERNO - ROTA ${routeNumber}`,
                decisionSummary: 'Soberania do operador: envio externo fixado manualmente.'
            });
            routeOrders.forEach(o => {
                o.optimizationOutcome = 'third';
                thirdPartyOrders.push(o);
                processedOrderIds.add(o.id);
            });
            totalThirdPartyCash += clusterPrice;
            totalThirdPartyDecision += clusterPrice;

            decisionTrace.push({
                kind: 'route',
                mode: 'third',
                decisionReason: 'UNKNOWN',
                orderIds: routeOrders.map(o => o.id),
                chosenLabel: `MANUAL_THIRD_${routeNumber}`,
                rationale: 'Decisao manual mantida pelo despachante (soberania do operador).',
                ownCost: internalAlternative.cashCost,
                thirdCost: clusterPrice,
                constraints: ['manual_assignment', 'manual_route_group']
            });
        }
    }

    if (autoPending.length === 0 && manualOrders.length > 0) {
        return buildFinalSuggestion(routes, ownFleetOrders, thirdPartyOrders, heldOrders, suggestedStrategies, totalOwnCash, totalThirdPartyCash, totalOwnDecision, totalThirdPartyDecision, config, primaryDecisionReason, decisionTrace, pulse.oracleMultiplier);
    }

    // 1. SIMULAÇÃO FROTA PRÓPRIA (Respeitando Raio de Atuação)
    const ownCandidates = await formClusters(autoPending, config.fleetConfig, config.businessLat, config.businessLng, config.googleMapsApiKey, config, 'own');

    for (const cluster of ownCandidates) {
        if (cluster.some(o => processedOrderIds.has(o.id))) continue;

        let lastLat = config.businessLat, lastLng = config.businessLng;
        let outboundMins = 0, interMins = 0;
        let totalAppCostForThisCluster = 0;
        let maxDistInCluster = 0;

        // PARALLEL: Pre-calculate all legs to minimize API latency
        const validOrders = cluster.filter(o => typeof o.lat === 'number' && typeof o.lng === 'number');
        const invalidOrders = cluster.filter(o => typeof o.lat !== 'number' || typeof o.lng !== 'number');

        // Handle invalid orders first (sequential for state consistency)
        for (const o of invalidOrders) {
            heldOrders.push(o);
            processedOrderIds.add(o.id);
            decisionTrace.push({
                kind: 'strategy',
                mode: 'hold',
                decisionReason: 'CONSTRAINT_NO_FLEET',
                orderIds: [o.id],
                chosenLabel: 'MISSING_COORDINATES',
                rationale: 'Pedido sem coordenadas válidas foi retido até a geocodificação ser concluída.',
                constraints: ['geocoding_missing']
            });
        }

        // PARALLEL: Pre-calculate all route legs and hub legs
        const legPromises = validOrders.map((o, i) => {
            const originLat = i === 0 ? config.businessLat : validOrders[i - 1].lat!;
            const originLng = i === 0 ? config.businessLng : validOrders[i - 1].lng!;
            return Promise.all([
                getTravelMinutes({
                    originLat, originLng, destLat: o.lat!, destLng: o.lng!,
                    googleMapsApiKey: config.googleMapsApiKey,
                    hereApiKey: config.hereMapsApiKey,
                    storeId,
                    orderCreatedAt: o.createdAt,
                    routingApiProvider: config.routingApiProvider,
                    routingApiFallbackEnabled: config.routingApiFallbackEnabled,
                    routingOsrmCalibration: config.routingOsrmCalibration
                }).catch(error => {
                    console.error(`[SMARTSPLIT] Route leg failed for order ${o.id}:`, error);
                    return { minutes: 0, distanceKm: 0 };
                }),
                getTravelMinutes({
                    originLat: config.businessLat,
                    originLng: config.businessLng,
                    destLat: o.lat!,
                    destLng: o.lng!,
                    googleMapsApiKey: config.googleMapsApiKey,
                    hereApiKey: config.hereMapsApiKey,
                    storeId,
                    orderCreatedAt: o.createdAt,
                    routingApiProvider: config.routingApiProvider,
                    routingApiFallbackEnabled: config.routingApiFallbackEnabled,
                    routingOsrmCalibration: config.routingOsrmCalibration
                }).catch(error => {
                    console.error(`[SMARTSPLIT] Hub leg failed for order ${o.id}:`, error);
                    return { minutes: 0, distanceKm: 0 };
                })
            ]);
        });

        const legs = await Promise.all(legPromises);

        // SEQUENTIAL: Process results with stateful updates
        const quotePromises: Promise<{ price: number; distForQuote: number; distFromHub: number; order: typeof validOrders[0] }>[] = [];
        
        for (let i = 0; i < validOrders.length; i++) {
            const o = validOrders[i];
            const [leg, hubLeg] = legs[i];

            if (i === 0) outboundMins = leg.minutes; else interMins += leg.minutes;
            o.estimatedDuration = leg.minutes;

            // v51.0: PRECISION ARBITRAGE (DistanceService)
            let distForQuote = leg.distanceKm > 0 ? leg.distanceKm : 0;
            
            // PARALLEL: Queue distance and quote calculations
            const distPromise = distForQuote === 0
                ? DistanceService.getDistance(lastLat, lastLng, o.lat!, o.lng!, 'precise').catch(() => 0)
                : Promise.resolve(distForQuote);

            const hubDistPromise = hubLeg.distanceKm > 0
                ? Promise.resolve(hubLeg.distanceKm)
                : DistanceService.getDistance(config.businessLat, config.businessLng, o.lat!, o.lng!, 'precise').catch(() => 0);

            quotePromises.push(
                Promise.all([distPromise, hubDistPromise]).then(async ([dist, hubDist]) => {
                    try {
                        const quote = await findBestProviderQuoteAsync(dist, o, providers, context, config, { lat: lastLat, lng: lastLng });
                        return { price: quote.price, distForQuote: dist, distFromHub: hubDist, order: o };
                    } catch (error) {
                        console.error(`[SMARTSPLIT] Quote failed for order ${o.id}:`, error);
                        return { price: 0, distForQuote: dist, distFromHub: hubDist, order: o };
                    }
                })
            );

            lastLat = o.lat!; lastLng = o.lng!;
        }

        // PARALLEL: Await all quote results
        const quotes = await Promise.all(quotePromises);
        
        for (const result of quotes) {
            // v57.5: Debug logging para detectar custo zero
            if (result.price === 0 || result.price > 9000) {
                console.error('[SMARTSPLIT] CUSTO SUSPEITO DETECTADO', {
                    orderId: result.order.id,
                    customerName: result.order.customerName,
                    distance: result.distForQuote,
                    quotedPrice: result.price,
                    activeProvidersCount: activeProviders.length
                });
            }

            totalAppCostForThisCluster += result.price;
            
            // v2025.02.08: Calcular distância REAL do HUB até este ponto
            if (result.distFromHub > maxDistInCluster) maxDistInCluster = result.distFromHub;

            // v55.5: Persist High-Precision Distance for UI Consistency (CostComparator)
            result.order.distance = result.distForQuote;
        }

        // Aplica desconto de rota app se houver agrupamento (Rota Inteligente)
        const batchDiscount = config.thirdPartyConfig?.batchDiscountPercentage ?? config.thirdPartySmartRouteDiscount ?? 0;
        if (cluster.length > 1) totalAppCostForThisCluster *= (1 - (batchDiscount / 100));

        // v56.3: Calculate REAL return time from last drop to hub (not assuming outbound equals return)
        const returnLeg = await getTravelMinutes({
            originLat: lastLat, originLng: lastLng, destLat: config.businessLat, destLng: config.businessLng,
            googleMapsApiKey: config.googleMapsApiKey,
            hereApiKey: config.hereMapsApiKey,
            storeId,
            orderCreatedAt: Date.now(),
            routingApiProvider: config.routingApiProvider,
            routingApiFallbackEnabled: config.routingApiFallbackEnabled,
            routingOsrmCalibration: config.routingOsrmCalibration
        });
        const returnMins = returnLeg.minutes || outboundMins; // Fallback to outbound if fails

        const internal = calculateInternalCostV2(
            { outbound: outboundMins, inter: interMins, return: returnMins },
            pulse.isBusy, cluster.length, pulse.loadFactor, config, pulse.oracleMultiplier
        );

        // Busca motoboy que aceite esta distância (Regra de Raio de Atuação)
        const eligibleSlotIdx = fleetSlots.findIndex(s =>
            s.isAvailable && (!s.driver.maxRangeKm || maxDistInCluster <= s.driver.maxRangeKm)
        );

        // v56.4: MARGIN-BASED ARBITRATION (Replaces 1.4x Threshold)
        // Optimize for REAL PROFIT (CustomerFee - Cost), not raw cost.
        const totalCustomerFees = cluster.reduce((sum, o) => sum + (o.deliveryFee || 0), 0);

        const costScoring = buildCanonicalCostScoringInput({
            ownCost: internal.cashCost,
            thirdCost: totalAppCostForThisCluster,
            customerFees: totalCustomerFees,
            minimumSavings: minSavings,
            deviationThreshold
        });

        // Fleet Preservation Bias: If fleet is idle AND internal is only slightly worse (< R$1), prefer internal
        const idleFleetBias = pulse.loadFactor < 0.5 ? 1.0 : 0; // R$ 1.00 tolerance if fleet is idle

        // Decision: Choose highest margin (or lowest loss), with fleet bias for ties
        let routeDecisionReason: import('../../../types').DecisionReason = 'CHEAPER';
        let isWorthThirdParty = costScoring.thirdPartyMargin > (costScoring.internalMargin + idleFleetBias) && costScoring.hasMinimumSavings;

        if (costScoring.exceedsDeviationThreshold && costScoring.savings > 0) {
            isWorthThirdParty = true;
        }

        // v48.5: PREDICTIVE AVAILABILITY CHECK (SLA Protection)
        // Se o motorista escolhido está retornando (delay > 0), verificamos se ele chega a tempo.
        if (eligibleSlotIdx !== -1 && !isWorthThirdParty) {
            const slot = fleetSlots[eligibleSlotIdx];
            if (slot.delayMins > 0) {
                // Calcula o Slack REAL (Considerando o tempo de viagem da entrega)
                // Slack = Deadline - Elapsed - Travel - Service (+ Tolerance)
                // Aqui usamos uma heurística rápida: Pior slack do cluster
                const sequencedStops = calculateSafeDispatchSequence(cluster, config, true);
                const worstSlack = buildCanonicalSlaSlackScoringInput(sequencedStops).minSlackMinutes;

                // Se o atraso do motorista consome toda a margem de segurança (Slack), terceiriza.
                if (slot.delayMins > worstSlack) {
                    isWorthThirdParty = true;
                    routeDecisionReason = 'SLA_PRIORITY';
                }
            }
        }

        // v46.9: PROTOCOLO DE PRESERVAÇÃO DE CAPACIDADE (Margin-Funded Shedding)
        // Se a rota sequestra o motoboy durante o pico E as taxas do cliente pagam o app, terceiriza forçado.
        if (oracle && kitchen && !isWorthThirdParty) {
            const totalRoundTripMins = outboundMins + interMins + returnMins; // Ida + Entregas + Volta real
            const safeWindow = calculateSafeReturnWindow(oracle, kitchen);

            const isRisky = isRouteRiskyForPeak(totalRoundTripMins, safeWindow);

            if (isRisky) {
                const totalCustomerFees = cluster.reduce((sum, o) => sum + (o.deliveryFee || 0), 0);
                // Se o cliente pagou o suficiente para cobrir o custo do App, preservamos a frota.
                if (totalCustomerFees >= costScoring.thirdCost) {
                    isWorthThirdParty = true; // FORCE OUTSOURCE
                    routeDecisionReason = 'CAPACITY_PRESSURE';
                }
            }
        }

        const sequencedStops = calculateSafeDispatchSequence(cluster, config, true);
        const minSlack = buildCanonicalSlaSlackScoringInput(sequencedStops).minSlackMinutes;

        if (eligibleSlotIdx !== -1 && !isWorthThirdParty) {
            const slot = fleetSlots[eligibleSlotIdx];
            const strategyPrefix = slot.requiresOvertimeApproval ? 'HORA EXTRA: ' : '';

            const { anyNotReady, allReady } = getClusterReadiness(cluster);
            const { boomerangSlackThreshold, dispatchHoldThreshold } = getDivergentThresholds(config);
            const oldestElapsedMins = getOldestElapsedMinutes(cluster);
            const holdAllowedByGuardrail = isHoldAllowedByGuardrail(
                oldestElapsedMins,
                cluster.length,
                maxHoldMinutes,
                maxHoldOrders
            );
            const routeDecisionSummary = slot.delayMins > 0
                ? `Motorista ${slot.driver.name} considerado por disponibilidade preditiva (retorno em ~${Math.ceil(slot.delayMins)} min).`
                : `Motorista ${slot.driver.name} elegivel agora com melhor margem para a frota propria.`;
            let strategySuggested = false;

            const readyOrders = cluster.filter(o => o.isReady || o.status === 'ready');
            const notReadyOrders = cluster.filter(o => !o.isReady && o.status !== 'ready');
            const serviceTime = config.fleetConfig.serviceTimeMinutes || 3;
            const firstLegTravel = Math.ceil(outboundMins * (Math.max(1, readyOrders.length) / cluster.length));
            const firstLegService = serviceTime * readyOrders.length;
            const secondLegTravel = Math.ceil(outboundMins * (Math.max(1, notReadyOrders.length) / cluster.length));
            const secondLegService = serviceTime * notReadyOrders.length;

            const divergentCandidates: Array<{
                id: 'boomerang' | 'dispatch_hold';
                tieBreaker: number;
                score: ArbitrationScore;
                suggestionRationale: string;
                traceRationale: string;
                orders: Order[];
                delayImpact: number;
                label: string;
            }> = [];

            if (
                anyNotReady &&
                !allReady &&
                holdAllowedByGuardrail &&
                minSlack > boomerangSlackThreshold &&
                !isStrategyIgnored(cluster, 'boomerang')
            ) {
                const boomerangDecision = createBoomerangDecision({
                    cluster,
                    slot,
                    internalCashCost: internal.cashCost,
                    thirdPartyCost: costScoring.thirdCost,
                    minSlack,
                    returnMins,
                    strategyPrefix,
                    serviceTimePerOrder: serviceTime,
                    config,
                    firstLegTiming: {
                        travelMinutes: firstLegTravel,
                        serviceMinutes: firstLegService,
                        totalMinutes: firstLegTravel + firstLegService
                    },
                    secondLegTiming: {
                        travelMinutes: secondLegTravel,
                        serviceMinutes: secondLegService,
                        totalMinutes: secondLegTravel + secondLegService
                    }
                });

                if (boomerangDecision) {
                    divergentCandidates.push({
                        id: 'boomerang',
                        tieBreaker: 0.02,
                        score: buildArbitrationScore({
                            sla: clamp01((minSlack - boomerangSlackThreshold + 10) / 24),
                            margin: 0.55,
                            capacity: clamp01(0.65 + ((readyOrders.length / Math.max(1, cluster.length)) * 0.25))
                        }, decisionScoringWeights),
                        suggestionRationale: `Estratégia Bumerangue: ${readyOrders.length} pedidos prontos + ${notReadyOrders.length} em produção. ` +
                            `Saída imediata, retorno para segunda leva. Economia estimada: R$ ${costScoring.savings.toFixed(2)}`,
                        traceRationale: boomerangDecision.trace.rationale,
                        orders: [...readyOrders, ...notReadyOrders],
                        delayImpact: 0,
                        label: 'BOOMERANG_SUGGESTED'
                    });
                }
            }

            if (
                !isDrainageActive &&
                anyNotReady &&
                holdAllowedByGuardrail &&
                minSlack > dispatchHoldThreshold &&
                !isStrategyIgnored(cluster, 'dispatch_hold')
            ) {
                const dispatchHoldDecision = createDispatchHoldDecision({
                    cluster,
                    slot,
                    internalCashCost: internal.cashCost,
                    thirdPartyCost: costScoring.thirdCost,
                    minSlack,
                    returnMins,
                    strategyPrefix,
                    serviceTimePerOrder: serviceTime,
                    config,
                    firstLegTiming: {
                        travelMinutes: firstLegTravel,
                        serviceMinutes: firstLegService,
                        totalMinutes: firstLegTravel + firstLegService
                    },
                    secondLegTiming: {
                        travelMinutes: secondLegTravel,
                        serviceMinutes: secondLegService,
                        totalMinutes: secondLegTravel + secondLegService
                    }
                });

                if (dispatchHoldDecision) {
                    divergentCandidates.push({
                        id: 'dispatch_hold',
                        tieBreaker: 0.01,
                        score: buildArbitrationScore({
                            sla: 0.65,
                            margin: 0.85,
                            capacity: clamp01(0.5 + ((notReadyOrders.length / Math.max(1, cluster.length)) * 0.25))
                        }, decisionScoringWeights),
                        suggestionRationale: `Despacho Retido: ${readyOrders.length} prontos aguardando ${notReadyOrders.length} em produção. ` +
                            `Rota consolidada economiza deslocamento. Aguardar ~${Math.ceil((config.averageKitchenTime || 20) * 0.6)}min.`,
                        traceRationale: dispatchHoldDecision.trace.rationale,
                        orders: cluster,
                        delayImpact: 8,
                        label: 'DISPATCH_HOLD_SUGGESTED'
                    });
                }
            }

            const selectedDivergent = pickArbitrationCandidate(
                divergentCandidates.map((candidate) => ({ id: candidate.id, score: candidate.score, tieBreaker: candidate.tieBreaker }))
            );

            if (selectedDivergent) {
                strategySuggested = true;
                const selectedCandidate = divergentCandidates.find((candidate) => candidate.id === selectedDivergent.id);
                if (selectedCandidate) {
                    const arbitrationSummary = formatArbitrationSummary(selectedCandidate.label, selectedCandidate.score, decisionPolicyProfile);

                    suggestedStrategies.push({
                        type: selectedCandidate.id,
                        orders: selectedCandidate.orders,
                        savings: Math.max(0, costScoring.savings),
                        delayImpact: selectedCandidate.delayImpact,
                        rationale: `${selectedCandidate.suggestionRationale} ${arbitrationSummary}`
                    });

                    decisionTrace.push({
                        kind: 'strategy',
                        mode: 'hold',
                        decisionReason: 'RETENTION_STRATEGY',
                        orderIds: cluster.map(o => o.id),
                        chosenLabel: selectedCandidate.label,
                        rationale: `${selectedCandidate.traceRationale} ${arbitrationSummary}`,
                        ownCost: internal.cashCost,
                        thirdCost: costScoring.thirdCost,
                        slackMin: minSlack,
                        driverDelayMin: slot.delayMins,
                        constraints: ['strategy_arbitration', `policy_${decisionPolicyProfile}`]
                    });
                }
            }

            if (!strategySuggested) {
                const marginSpread = Math.max(1, Math.abs(costScoring.internalMargin - costScoring.thirdPartyMargin));
                const ownScore = buildArbitrationScore({
                    sla: clamp01((minSlack - slot.delayMins + 10) / 22),
                    margin: clamp01(0.5 + ((costScoring.internalMargin - costScoring.thirdPartyMargin) / (2 * marginSpread))),
                    capacity: slot.requiresOvertimeApproval ? 0.45 : 0.75
                }, decisionScoringWeights);
                const thirdScore = buildArbitrationScore({
                    sla: routeDecisionReason === 'SLA_PRIORITY' ? 0.95 : clamp01((minSlack + 8) / 24),
                    margin: clamp01(0.5 + ((costScoring.thirdPartyMargin - costScoring.internalMargin) / (2 * marginSpread))),
                    capacity: routeDecisionReason === 'CAPACITY_PRESSURE' ? 0.95 : 0.6
                }, decisionScoringWeights);

                const routeCandidates = [
                    { id: 'own' as const, score: ownScore, tieBreaker: isWorthThirdParty ? 0 : 0.02 },
                    { id: 'third' as const, score: thirdScore, tieBreaker: isWorthThirdParty ? 0.03 : 0 }
                ];
                const selectedRoute = pickArbitrationCandidate(routeCandidates);

                if (selectedRoute?.id === 'own') {
                    const arbitrationSummary = formatArbitrationSummary('OWN_FLEET', ownScore, decisionPolicyProfile);

                    routes.push({
                        type: 'own', assigneeId: slot.driverId, assigneeName: slot.driver.name,
                        orders: cluster, cost: internal.cashCost,
                        strategyLabel: slot.requiresOvertimeApproval ? 'HORA EXTRA SOLICITADA' : undefined,
                        decisionSummary: `${routeDecisionSummary} ${arbitrationSummary}`,
                        estimatedReturnMinutes: returnMins,
                        requiresOvertimeApproval: slot.requiresOvertimeApproval
                    });
                    cluster.forEach(o => { ownFleetOrders.push(o); processedOrderIds.add(o.id); });

                    decisionTrace.push({
                        kind: 'route',
                        mode: 'own',
                        decisionReason: 'CHEAPER',
                        orderIds: cluster.map(o => o.id),
                        chosenLabel: 'OWN_FLEET',
                        rationale: `${routeDecisionSummary} ${arbitrationSummary}`,
                        ownCost: internal.cashCost,
                        thirdCost: costScoring.thirdCost,
                        slackMin: minSlack,
                        driverDelayMin: slot.delayMins,
                        constraints: ['cone', 'zigzag', 'max_radius', 'bag_limit', 'driver_range', 'candidate_arbitration', `policy_${decisionPolicyProfile}`]
                    });

                    totalOwnCash += internal.cashCost;
                    totalOwnDecision += internal.totalDecisionCost;
                    fleetSlots.splice(eligibleSlotIdx, 1);
                } else {
                    const thirdPartyRationale = routeDecisionReason === 'CAPACITY_PRESSURE'
                        ? 'Terceirizacao ativada para preservar capacidade da frota antes do pico previsto pelo Oracle.'
                        : routeDecisionReason === 'SLA_PRIORITY'
                            ? 'Terceirizacao ativada para proteger SLA diante de risco de atraso com a frota.'
                            : 'Apps parceiros entregam maior margem liquida no cenario atual.';
                    const arbitrationSummary = formatArbitrationSummary('THIRD_PARTY', thirdScore, decisionPolicyProfile);

                    decisionTrace.push({
                        kind: 'route',
                        mode: 'third',
                        decisionReason: routeDecisionReason,
                        orderIds: cluster.map(o => o.id),
                        chosenLabel: 'THIRD_PARTY',
                        rationale: `${thirdPartyRationale} ${arbitrationSummary}`,
                        ownCost: internal.cashCost,
                        thirdCost: costScoring.thirdCost,
                        constraints: ['economic_priority', routeDecisionReason, `min_savings_${minSavings.toFixed(2)}`, `deviation_${Number.isFinite(deviationThreshold) ? deviationThreshold.toFixed(2) : 'off'}`, 'candidate_arbitration', `policy_${decisionPolicyProfile}`]
                    });
                    primaryDecisionReason = routeDecisionReason;
                }
            }
        } else if (isWorthThirdParty) {
            const thirdScore = buildArbitrationScore({
                sla: routeDecisionReason === 'SLA_PRIORITY' ? 0.95 : clamp01((minSlack + 8) / 24),
                margin: 0.8,
                capacity: routeDecisionReason === 'CAPACITY_PRESSURE' ? 0.95 : 0.75
            }, decisionScoringWeights);
            const thirdPartyRationale = routeDecisionReason === 'CAPACITY_PRESSURE'
                ? 'Terceirizacao ativada para preservar capacidade da frota antes do pico previsto pelo Oracle.'
                : routeDecisionReason === 'SLA_PRIORITY'
                    ? 'Terceirizacao ativada para proteger SLA diante de risco de atraso com a frota.'
                    : 'Apps parceiros entregam maior margem liquida no cenario atual.';
            const arbitrationSummary = formatArbitrationSummary('THIRD_PARTY', thirdScore, decisionPolicyProfile);

            decisionTrace.push({
                kind: 'route',
                mode: 'third',
                decisionReason: routeDecisionReason,
                orderIds: cluster.map(o => o.id),
                chosenLabel: 'THIRD_PARTY',
                rationale: `${thirdPartyRationale} ${arbitrationSummary}`,
                ownCost: internal.cashCost,
                thirdCost: costScoring.thirdCost,
                constraints: ['economic_priority', routeDecisionReason, `min_savings_${minSavings.toFixed(2)}`, `deviation_${Number.isFinite(deviationThreshold) ? deviationThreshold.toFixed(2) : 'off'}`, 'candidate_arbitration', `policy_${decisionPolicyProfile}`]
            });
            primaryDecisionReason = routeDecisionReason;
        } else if (eligibleSlotIdx === -1 && !isWorthThirdParty) {
            const waitScore = buildArbitrationScore({
                sla: clamp01((minSlack + 6) / 22),
                margin: 0.25,
                capacity: 0.1
            }, decisionScoringWeights);
            const arbitrationSummary = formatArbitrationSummary('WAIT_FLEET', waitScore, decisionPolicyProfile);

            decisionTrace.push({
                kind: 'route',
                mode: 'own',
                decisionReason: 'CONSTRAINT_NO_FLEET',
                orderIds: cluster.map(o => o.id),
                chosenLabel: 'WAIT_FLEET',
                rationale: `Sem motoboy elegivel no momento e terceirizacao nao viavel neste cenario. ${arbitrationSummary}`,
                ownCost: internal.cashCost,
                thirdCost: costScoring.thirdCost,
                constraints: ['driver_unavailable', costScoring.hasThirdPartyCost ? 'third_not_profitable' : 'third_provider_missing', 'candidate_arbitration', `policy_${decisionPolicyProfile}`]
            });
        }
    }

    // 2. RETENÇÃO DE PRODUÇÃO (Pedidos Solitários com muito prazo)
    const remainingForApp = autoPending.filter(o => !processedOrderIds.has(o.id));
    const retentionThreshold = Math.max(
        (config.averageKitchenTime || 10) + 2,
        config.fleetConfig.slaToleranceMinutes || 10
    );
    for (const o of remainingForApp) {
        const analysis = analyzeSafeDispatch(o, config, 0, true, [], undefined, 1); // v56.1: Solitário sempre = routeSize 1
        const elapsed = (Date.now() - (o.createdAt || Date.now())) / 60000;
        // Se pode esperar sem comprometer produção + entrega (sem tolerância)
        // v48.4: Desativa Retenção se estiver em Drenagem (Fechamento)
        // Calcula tempo de vida útil restante do pedido (tempo até SLA crítico)
        const timeLimit = o.timeLimit || 60; // minutos padrão
        const remainingLifetime = timeLimit - elapsed;
        
        const retentionDecision = evaluateProductionRetention({
            order: o,
            isDrainageActive,
            elapsedMinutes: elapsed,
            maxHoldMinutes,
            retentionThreshold,
            analysisSlackMinutes: analysis.value,
            remainingLifetimeMinutes: remainingLifetime
        });
        if (retentionDecision.hold) {
            const retentionSavings = retentionDecision.trace
                ? Math.max(0, (retentionDecision.trace.thirdCost ?? 0) - (retentionDecision.trace.ownCost ?? 0))
                : 0;
            const retentionScore = buildArbitrationScore({
                sla: clamp01((analysis.value - retentionThreshold + 8) / 22),
                margin: clamp01(0.5 + Math.min(0.5, retentionSavings / Math.max(1, retentionThreshold))),
                capacity: 0.6
            }, decisionScoringWeights);
            const retentionArbitrationSummary = formatArbitrationSummary('PRODUCTION_RETENTION_SUGGESTED', retentionScore, decisionPolicyProfile);

            // v59.0: Retenção de Produção como SUGESTÃO (não automática)
            // O pedido permanece nas colunas, aguardando confirmação do operador
            suggestedStrategies.push({
                type: 'production_retention',
                orders: [o],
                savings: retentionSavings,
                delayImpact: 5,
                rationale: `Retenção de Produção: Pedido solitário com ${remainingLifetime.toFixed(0)}min de vida útil. ` +
                          `Aguardar formar par logístico para economia de rota compartilhada. ${retentionArbitrationSummary}`
            });

            if (retentionDecision.trace) {
                decisionTrace.push({
                    ...retentionDecision.trace,
                    chosenLabel: 'PRODUCTION_RETENTION_SUGGESTED',
                    rationale: `Retenção de Produção sugerida - aguardando confirmação do operador via modal Sofia. ${retentionArbitrationSummary}`,
                    constraints: [...(retentionDecision.trace.constraints || []), 'candidate_arbitration', `policy_${decisionPolicyProfile}`]
                });
            }

            // NÃO move para heldOrders automaticamente - aguarda confirmação
            // O pedido continua disponível nas colunas para despacho
        }
    }

    // 3. APPS EXTERNOS (O que sobrou e não foi retido)
    const finalForApp = autoPending.filter(o => !processedOrderIds.has(o.id));
    if (finalForApp.length > 0) {
        const appCandidates = await formClusters(finalForApp, config.thirdPartyConfig, config.businessLat, config.businessLng, config.googleMapsApiKey, config, 'third');

        let courierCounter = 1;

        for (const cluster of appCandidates) {
            const unp = cluster.filter(o => !processedOrderIds.has(o.id));
            if (unp.length === 0) continue;

            if (activeProviders.length === 0) {
                decisionTrace.push({
                    kind: 'route',
                    mode: 'own',
                    decisionReason: 'CONSTRAINT_NO_FLEET',
                    orderIds: unp.map(o => o.id),
                    chosenLabel: 'NO_THIRD_PROVIDER',
                    rationale: 'Sem parceiros ativos para terceirizacao. Pedido segue aguardando alocacao da frota.',
                    constraints: ['third_party_provider_missing']
                });
                primaryDecisionReason = 'CONSTRAINT_NO_FLEET';
                continue;
            }

            // v50.4: Cálculo Corrigido - Rota de Parceiro Único
            // PARALLEL: Pre-calculate all route legs to minimize API latency
            const distances: number[] = [];
            let lastLat = config.businessLat, lastLng = config.businessLng;
            let outboundMins = 0;
            let interMins = 0;
            let maxDistInCluster = 0;

            // Filter valid orders first
            const validUnp = unp.filter(o => typeof o.lat === 'number' && typeof o.lng === 'number');
            const invalidUnp = unp.filter(o => typeof o.lat !== 'number' || typeof o.lng !== 'number');

            // Handle invalid orders
            for (const o of invalidUnp) {
                heldOrders.push(o);
                processedOrderIds.add(o.id);
                decisionTrace.push({
                    kind: 'strategy',
                    mode: 'hold',
                    decisionReason: 'CONSTRAINT_NO_FLEET',
                    orderIds: [o.id],
                    chosenLabel: 'MISSING_COORDINATES',
                    rationale: 'Pedido sem coordenadas válidas foi retido até a geocodificação ser concluída.',
                    constraints: ['geocoding_missing']
                });
            }

            // PARALLEL: Pre-calculate all route legs
            const legPromises = validUnp.map((o, i) => {
                const originLat = i === 0 ? config.businessLat : validUnp[i - 1].lat!;
                const originLng = i === 0 ? config.businessLng : validUnp[i - 1].lng!;
                return Promise.all([
                    getTravelMinutes({
                        originLat, originLng, destLat: o.lat!, destLng: o.lng!,
                        googleMapsApiKey: config.googleMapsApiKey,
                        hereApiKey: config.hereMapsApiKey,
                        storeId,
                        orderCreatedAt: o.createdAt,
                        routingApiProvider: config.routingApiProvider,
                        routingApiFallbackEnabled: config.routingApiFallbackEnabled,
                        routingOsrmCalibration: config.routingOsrmCalibration
                    }).catch(error => {
                        console.error(`[SMARTSPLIT] Route leg failed for order ${o.id}:`, error);
                        return { minutes: 0, distanceKm: 0 };
                    }),
                    DistanceService.getDistance(config.businessLat, config.businessLng, o.lat!, o.lng!, 'precise').catch(() => 0)
                ]);
            });

            const legResults = await Promise.all(legPromises);

            // SEQUENTIAL: Process results with stateful updates
            for (let idx = 0; idx < validUnp.length; idx++) {
                const o = validUnp[idx];
                const [leg, distFromHub] = legResults[idx];
                
                let d = leg.distanceKm > 0 ? leg.distanceKm : 0;
                if (idx === 0) outboundMins = leg.minutes; else interMins += leg.minutes;

                // v50.7: PRECISION ROUTING (DistanceService) - only if leg returned 0
                if (d === 0) {
                    try {
                        d = await DistanceService.getDistance(lastLat, lastLng, o.lat!, o.lng!, 'precise');
                    } catch (error) {
                        console.error(`[SMARTSPLIT] Distance fallback failed for order ${o.id}:`, error);
                    }
                }

                // v50.8: Persist the High-Precision Distance for UI Display
                o.distance = d;
                o.estimatedDuration = leg.minutes;

                distances.push(d);
                if (distFromHub > maxDistInCluster) maxDistInCluster = distFromHub;
                lastLat = o.lat!; lastLng = o.lng!;
            }

            // 2. Encontra o ÚNICO parceiro mais barato para TODO o cluster
            // PARALLEL: Calculate all provider quotes in parallel
            const providerQuoteResults = await Promise.all(
                activeProviders.map(async (p) => {
                    // Simula custo se entregássemos tudo com este parceiro (somado)
                    // Nota: O desconto de batch será aplicado ao total, simulando ganho de rota
                    const quoteResults = await Promise.all(
                        unp.map(async (orderForQuote, idx) => {
                            try {
                                const d = distances[idx];
                                const q = await findBestProviderQuoteAsync(d, orderForQuote, [p], context, config);
                                return q.price;
                            } catch (error) {
                                console.error('[SMARTSPLIT] Provider quote error', { providerId: p.id, providerName: p.name, error });
                                return Infinity;
                            }
                        })
                    );
                    const providerTotal = quoteResults.reduce((sum, price) => sum + (price === Infinity ? Infinity : price), 0);
                    return { provider: p, total: providerTotal };
                })
            );

            const bestProvider = providerQuoteResults.find(r => Number.isFinite(r.total) && r.total < Infinity);
            let bestName = bestProvider?.provider.name || 'App';
            let bestPrice = bestProvider?.total ?? Infinity;

            let clusterPrice = bestPrice;
            const batchDiscount = config.thirdPartyConfig?.batchDiscountPercentage ?? config.thirdPartySmartRouteDiscount ?? 0;
            if (unp.length > 1) clusterPrice *= (1 - (batchDiscount / 100));

            if (!Number.isFinite(clusterPrice) || clusterPrice <= 0) {
                decisionTrace.push({
                    kind: 'route',
                    mode: 'own',
                    decisionReason: 'CONSTRAINT_NO_FLEET',
                    orderIds: unp.map(o => o.id),
                    chosenLabel: 'THIRD_COST_INVALID',
                    rationale: 'Custo externo invalido detectado. Pedido segue aguardando alocacao valida.',
                    constraints: ['third_cost_invalid']
                });
                continue;
            }

            const label = `${bestName}: MOTOBOY ${courierCounter++}`;

            const returnLeg = await getTravelMinutes({
                originLat: lastLat,
                originLng: lastLng,
                destLat: config.businessLat,
                destLng: config.businessLng,
                googleMapsApiKey: config.googleMapsApiKey,
                hereApiKey: config.hereMapsApiKey,
                storeId,
                orderCreatedAt: Date.now(),
                routingApiProvider: config.routingApiProvider,
                routingApiFallbackEnabled: config.routingApiFallbackEnabled,
                routingOsrmCalibration: config.routingOsrmCalibration
            });

            const ownAlternative = calculateInternalCostV2(
                { outbound: outboundMins, inter: interMins, return: returnLeg.minutes },
                pulse.isBusy,
                unp.length,
                pulse.loadFactor,
                config,
                pulse.oracleMultiplier
            );

            const fallbackSlotIdx = fleetSlots.findIndex(s =>
                s.isAvailable && (!s.driver.maxRangeKm || maxDistInCluster <= s.driver.maxRangeKm)
            );
            const hasEligibleOwnSlot = fallbackSlotIdx !== -1;
            const feeTotal = unp.reduce((sum, order) => sum + (order.deliveryFee || 0), 0);
            const ownMargin = feeTotal - ownAlternative.cashCost;
            const thirdMargin = feeTotal - clusterPrice;
            const marginSpread = Math.max(1, Math.abs(ownMargin - thirdMargin));

            const ownClusterScore = hasEligibleOwnSlot
                ? buildArbitrationScore({
                    sla: 0.72,
                    margin: clamp01(0.5 + ((ownMargin - thirdMargin) / (2 * marginSpread))),
                    capacity: (fleetSlots[fallbackSlotIdx]?.requiresOvertimeApproval ? 0.45 : 0.75)
                }, decisionScoringWeights)
                : null;
            const thirdClusterScore = buildArbitrationScore({
                sla: hasEligibleOwnSlot ? 0.65 : 0.92,
                margin: clamp01(0.5 + ((thirdMargin - ownMargin) / (2 * marginSpread))),
                capacity: hasEligibleOwnSlot ? 0.6 : 0.95
            }, decisionScoringWeights);

            const clusterCandidates: ArbitrationCandidate<'own' | 'third'>[] = [];
            if (ownClusterScore) {
                clusterCandidates.push({ id: 'own', score: ownClusterScore, tieBreaker: ownAlternative.cashCost <= clusterPrice ? 0.02 : 0 });
            }
            clusterCandidates.push({ id: 'third', score: thirdClusterScore, tieBreaker: hasEligibleOwnSlot ? 0 : 0.03 });

            const selectedClusterCandidate = pickArbitrationCandidate(clusterCandidates);

            if (selectedClusterCandidate?.id === 'own' && ownClusterScore && hasEligibleOwnSlot) {
                const slot = fleetSlots[fallbackSlotIdx];
                const arbitrationSummary = formatArbitrationSummary('OWN_CLUSTER', ownClusterScore, decisionPolicyProfile);

                routes.push({
                    type: 'own',
                    assigneeId: slot.driverId,
                    assigneeName: slot.driver.name,
                    orders: unp,
                    cost: ownAlternative.cashCost,
                    strategyLabel: slot.requiresOvertimeApproval ? 'HORA EXTRA SOLICITADA' : undefined,
                    decisionSummary: `Rota interna escolhida por menor custo real da frota (vs ${bestName}). ${arbitrationSummary}`,
                    estimatedReturnMinutes: returnLeg.minutes,
                    requiresOvertimeApproval: slot.requiresOvertimeApproval
                });
                unp.forEach(o => { ownFleetOrders.push(o); processedOrderIds.add(o.id); });
                totalOwnCash += ownAlternative.cashCost;
                totalOwnDecision += ownAlternative.totalDecisionCost;
                fleetSlots.splice(fallbackSlotIdx, 1);

                decisionTrace.push({
                    kind: 'route',
                    mode: 'own',
                    decisionReason: 'CHEAPER',
                    orderIds: unp.map(o => o.id),
                    chosenLabel: 'OWN_CLUSTER',
                    rationale: `Frota propria escolhida por menor custo real da rota (vs parceiro ${bestName}). ${arbitrationSummary}`,
                    ownCost: ownAlternative.cashCost,
                    thirdCost: clusterPrice,
                    constraints: ['cluster_recheck', 'economic_priority', 'driver_range', 'candidate_arbitration', `policy_${decisionPolicyProfile}`]
                });
                continue;
            }

            const thirdDecisionReason: import('../../../types').DecisionReason = hasEligibleOwnSlot
                ? 'CHEAPER'
                : 'CONSTRAINT_NO_FLEET';
            const thirdRationale = hasEligibleOwnSlot
                ? `Terceirizacao escolhida com parceiro ${bestName} pelo menor custo da rota.`
                : `Terceirizacao escolhida com parceiro ${bestName} por indisponibilidade de motoboy elegivel no momento.`;
            const arbitrationSummary = formatArbitrationSummary('THIRD_CLUSTER', thirdClusterScore, decisionPolicyProfile);

            routes.push({
                type: 'third',
                assigneeName: label,
                orders: unp,
                cost: clusterPrice,
                decisionSummary: thirdDecisionReason === 'CHEAPER'
                    ? `Cluster alocado para ${bestName} por melhor custo total entre parceiros ativos. ${arbitrationSummary}`
                    : `Cluster alocado para ${bestName} por restricao de disponibilidade da frota interna. ${arbitrationSummary}`
            });
            unp.forEach(o => { thirdPartyOrders.push(o); processedOrderIds.add(o.id); });
            totalThirdPartyCash += clusterPrice;
            totalThirdPartyDecision += clusterPrice;

            decisionTrace.push({
                kind: 'route',
                mode: 'third',
                decisionReason: thirdDecisionReason,
                orderIds: unp.map(o => o.id),
                chosenLabel: 'THIRD_CLUSTER',
                rationale: `${thirdRationale} ${arbitrationSummary}`,
                ownCost: ownAlternative.cashCost,
                thirdCost: clusterPrice,
                constraints: thirdDecisionReason === 'CHEAPER'
                    ? ['third_party_profile', 'batch_discount', 'provider_table', 'economic_priority', 'candidate_arbitration', `policy_${decisionPolicyProfile}`]
                    : ['third_party_profile', 'batch_discount', 'provider_table', 'driver_unavailable', 'candidate_arbitration', `policy_${decisionPolicyProfile}`]
            });
            if (thirdDecisionReason !== 'CHEAPER') {
                primaryDecisionReason = 'CONSTRAINT_NO_FLEET';
            }
        }
    }

    return buildFinalSuggestion(routes, ownFleetOrders, thirdPartyOrders, heldOrders, suggestedStrategies, totalOwnCash, totalThirdPartyCash, totalOwnDecision, totalThirdPartyDecision, config, primaryDecisionReason, decisionTrace, pulse.oracleMultiplier);
};

const createEmptySuggestion = (): SmartSplitSuggestion => ({
    ownFleet: [], thirdParty: [], heldOrders: [], routes: [], thirdPartyTripsCount: 0,
    oracleMultiplier: 1,
    metrics: {
        breakdown: { ownCash: 0, thirdPartyCash: 0, ownDecision: 0, thirdPartyDecision: 0 },
        cashDelta: { chosen: 0, alternative: 0, signedDelta: 0, label: 'ECONOMIA_CAIXA', reason: 'UNKNOWN' },
        decisionStats: { totalDecisions: 0, byReason: {}, byMode: {} }
    },
    isSplitRecommended: false, optimizationConstrainedBySLA: false, estimatedTime: 0, minSla: 999, lowestSlack: 999,
    decisionTrace: []
});

const buildFinalSuggestion = (
    routes: any[], own: any[], third: any[], held: any[],
    suggestedStrategies: import('../../../types').SmartSplitSuggestion['suggestedStrategies'] = [],
    ownC: number, thirdC: number, ownD: number, thirdD: number,
    config?: SystemConfig,
    decisionReason: import('../../../types').DecisionReason = 'CHEAPER',
    decisionTrace: DecisionTraceEntry[] = [],
    oracleMultiplier: number = 1
): SmartSplitSuggestion => {
    const readyHeld = held.filter((o: any) => o.status === 'ready' || o.isReady);
    const inKitchenHeld = held.filter((o: any) => o.status !== 'ready' && !o.isReady);

    // v59.0: Coleta pedidos de estratégias sugeridas para análise
    const suggestedOrders = suggestedStrategies?.flatMap(s => s.orders) || [];
    const allHeldOrSuggested = [...held, ...suggestedOrders];
    
    const now = Date.now();
    const minSlack = allHeldOrSuggested.length > 0
        ? Math.min(...allHeldOrSuggested.map(o => {
            const elapsed = (now - (o.createdAt || now)) / 60000;
            return (o.timeLimit || 45) - elapsed;
        }))
        : 999;

    // Calcula economia total incluindo sugestões
    const projectedSavingsFromTrace = decisionTrace
        .filter(t => t.kind === 'strategy' && (t.mode === 'hold' || t.chosenLabel?.includes('SUGGESTED')))
        .reduce((acc, t) => {
            const ownCost = t.ownCost ?? 0;
            const thirdCost = t.thirdCost ?? 0;
            return acc + Math.max(0, thirdCost - ownCost);
        }, 0);
    
    // Adiciona economia das estratégias sugeridas
    const totalProjectedSavings = suggestedStrategies?.reduce((acc, s) => acc + (s.savings || 0), projectedSavingsFromTrace) || projectedSavingsFromTrace;

    const decisionStats = decisionTrace.reduce((acc, trace) => {
        const reason = trace.decisionReason || 'UNKNOWN';
        const mode = trace.mode || 'own';

        acc.totalDecisions += 1;
        acc.byReason[reason] = (acc.byReason[reason] || 0) + 1;
        acc.byMode[mode] = (acc.byMode[mode] || 0) + 1;
        return acc;
    }, {
        totalDecisions: 0,
        byReason: {} as Record<string, number>,
        byMode: {} as Record<string, number>
    });

    // v59.0: smartWaitAnalysis inclui pedidos sugeridos (aguardando confirmação)
    const hasSuggestedRetention = suggestedStrategies?.some(s => s.type === 'dispatch_hold' || s.type === 'production_retention');
    const hasSuggestedBoomerang = suggestedStrategies?.some(s => s.type === 'boomerang');
    const readyFromSuggestions = suggestedOrders.filter((o: any) => o.status === 'ready' || o.isReady);
    const kitchenFromSuggestions = suggestedOrders.filter((o: any) => o.status !== 'ready' && !o.isReady);
    
    const smartWaitAnalysis = (held.length > 0 || hasSuggestedRetention) ? {
        active: true,
        readyOrders: [...readyHeld, ...readyFromSuggestions],
        kitchenOrders: [...inKitchenHeld, ...kitchenFromSuggestions],
        projectedSavings: totalProjectedSavings > 0 ? totalProjectedSavings : (allHeldOrSuggested.length * 2),
        delayImpact: 8,
        isCritical: minSlack < 10,
        message: (readyHeld.length + readyFromSuggestions.length) > 0
            ? `Segure os pedidos prontos. Vamos mandar tudo junto com os que estão saindo do forno.`
            : `Segure este pedido um pouco. Vale a pena esperar cair mais entregas para essa região.`,
        // v59.0: Indica que há estratégias sugeridas aguardando confirmação
        hasSuggestedStrategies: suggestedStrategies && suggestedStrategies.length > 0
    } : null;

    return {
        ownFleet: own,
        thirdParty: third,
        heldOrders: held,
        oracleMultiplier,
        routes,
        thirdPartyTripsCount: routes.filter(r => r.type === 'third').length,
        metrics: {
            breakdown: { ownCash: ownC, thirdPartyCash: thirdC, ownDecision: ownD, thirdPartyDecision: thirdD },
            cashDelta: { chosen: ownC + thirdC, alternative: 0, signedDelta: 0, label: 'EFICIÊNCIA_ATUAL', reason: decisionReason },
            decisionStats
        },
        isSplitRecommended: true,
        optimizationConstrainedBySLA: false,
        estimatedTime: 0,
        minSla: 0,
        lowestSlack: 0,
        smartWaitAnalysis,
        decisionTrace,
        // v59.0: Inclui estratégias sugeridas no retorno
        suggestedStrategies,
        // v59.0: Constrói sequentialOpportunity a partir de estratégias sugeridas (boomerang)
        sequentialOpportunity: hasSuggestedBoomerang ? (() => {
            const boomerangStrategy = suggestedStrategies?.find(s => s.type === 'boomerang');
            if (!boomerangStrategy) return undefined;
            const readyOrders = boomerangStrategy.orders.filter((o: any) => o.status === 'ready' || o.isReady);
            const notReadyOrders = boomerangStrategy.orders.filter((o: any) => o.status !== 'ready' && !o.isReady);
            return {
                active: true,
                driverName: 'Motoboy disponível', // será atualizado pelo slot real quando confirmado
                trip1: readyOrders,
                trip2: notReadyOrders,
                projectedSavings: boomerangStrategy.savings,
                rationale: boomerangStrategy.rationale
            };
        })() : undefined
    };
};
